import React, { Suspense, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { getOrCreateCheckout, initClient, toggleCart } from "../../store/actions";
import { I18nextProvider, useTranslation } from "react-i18next";
import { LANGUAGE_FR_CODE } from "../../constants/AppConstants";
import { SET_CURRENT_LANGUAGE } from "../../constants/ActionTypes";
import i18next from "i18next";
import i18n from "../../../../i18n/i18n";
import CartEventTracker from "../../utils/events";
import { cartLinesAddMutation } from "../../utils/helpers";

const AddToCart = ({ productId }) => {
  const { t } = useTranslation(["checkout"]);
  const dispatch = useDispatch();
  const [isAddingToCart, setIsAddingToCart] = React.useState(false);
  const { client } = useSelector(state => state.shopify);
  const { checkout } = useSelector(state => state.checkout);
  const langCode = document.getElementById("language-code")?.value || LANGUAGE_FR_CODE;
  const [availableForSaleError, setAvailableForSaleError] = React.useState(false);
  const cartTracker = new CartEventTracker();
  const [hasInitialized, setHasInitialized] = React.useState(false);

  useEffect(() => {
    dispatch({
      type: SET_CURRENT_LANGUAGE,
      payload: langCode,
    });
    if (langCode?.length === 5) {
      i18next.changeLanguage(langCode);
    }
    dispatch(initClient());
  }, []);

  useEffect(() => {

    if (!client || hasInitialized) return;

    const initAddToCartLabel = async () => {
      setHasInitialized(true);

      const shopifyProductId = `gid://shopify/Product/${productId}`;
      const productQuery = `
      query ProductQuery($id: ID!) {
        node(id: $id) {
          ... on Product {
            id
            title
            handle
            availableForSale
            variants(first: 250) {
              edges {
                node {
                  id
                  availableForSale
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
      }
    `;

    client
      .fetch(productQuery, {
        variables: {
          id: shopifyProductId,
        },
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
        }
      })
      .then(({ data, errors }) => {
        if (errors && errors.length > 0) {
          console.error("GraphQL errors:", errors);
          setAvailableForSaleError(t("Product not available"));
          return;
        }

        const product = data?.node;
        if (product) {
          if (!product?.availableForSale) {
            setAvailableForSaleError(
                langCode === LANGUAGE_FR_CODE ? "Épuisé en ligne" : "Out of stock"
            );
            return;
          }
          const availableVariant = product.variants.edges.find(edge => edge.node.availableForSale);
          if (!availableVariant) {
            setAvailableForSaleError(
                langCode === LANGUAGE_FR_CODE ? "Épuisé en ligne" : "Out of stock"
            );
            return;
          }
        }
      });
    };
    try {
      initAddToCartLabel();
    } catch (e) {
      console.log(e);
    }
  }, [client, hasInitialized]) ;


  /**
   * When a user Click the add to cart button
   * We fetch the product from the shopify store
   * and add it to the cart
   *
   * @returns {Promise<void>}
   */
  const handleAddToCart = async () => {
    await dispatch(initClient());
    dispatch(getOrCreateCheckout());
    setIsAddingToCart(true);
    const shopifyProductId = `gid://shopify/Product/${productId}`;

    const productQuery = `
    query ProductQuery($id: ID!) {
      node(id: $id) {
        ... on Product {
          id
          title
          handle
          availableForSale
          variants(first: 250) {
            edges {
              node {
                id
                availableForSale
                price {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
  `;

    client
      .fetch(productQuery, {
        variables: {
          id: shopifyProductId,
        },
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
        }
      })
      .then(({ data, errors }) => {
        if (errors && errors.length > 0) {
          console.error("GraphQL errors:", errors);
          setIsAddingToCart(false);
          setAvailableForSaleError(t("Product not available"));
          return;
        }

        const product = data?.node;

        if (product) {
          if (!product?.availableForSale) {
            setIsAddingToCart(false);
            setAvailableForSaleError(
              langCode === LANGUAGE_FR_CODE ? "Épuisé en ligne" : "Out of stock"
            );
            return;
          }
          const availableVariant = product.variants.edges.find(edge => edge.node.availableForSale);
          if (!availableVariant) {
            setIsAddingToCart(false);
            setAvailableForSaleError(
              langCode === LANGUAGE_FR_CODE ? "Épuisé en ligne" : "Out of stock"
            );
            return;
          }

          const firstVariant = availableVariant.node;
          client
            .fetch(cartLinesAddMutation, {
              variables: {
                cartId: checkout?.id,
                lines: [
                  {
                    merchandiseId: firstVariant.id,
                    quantity: 1,
                  },
                ],
              },
            })
            .then(response => {
              if (response.ok) {
                return response.json();
              } else {
                throw new Error(`HTTP error: ${response.status}`);
              }
            })
            .then(({ data, errors }) => {
              if (errors && errors.length > 0) {
                console.error("Cart mutation errors:", errors);
                setIsAddingToCart(false);
                return;
              }

              if (data?.cartLinesAdd?.userErrors?.length > 0) {
                console.error("Cart user errors:", data.cartLinesAdd.userErrors);
                setIsAddingToCart(false);
                return;
              }

              // Find the added item in the updated cart
              const updatedCart = data?.cartLinesAdd?.cart;
              const itemDetails = updatedCart?.lines?.edges?.find(
                edge => edge.node.merchandise.id === firstVariant.id
              )?.node;
              dispatch(toggleCart(true));
              setIsAddingToCart(false);
              cartTracker.addToCartEvent(itemDetails);
            })
            .catch(error => {
              setIsAddingToCart(false);
              console.log("Cart add error:", error);
            });
        } else {
          // This means we are adding products that is not in correct store
          // (Like we are in usa store and adding product from canada store)
          setIsAddingToCart(false);
          setAvailableForSaleError(t("Product not available"));
          console.error("Product not found, More likely you are adding product from wrong store");
        }
      })
      .catch(error => {
        setIsAddingToCart(false);
        console.log("Product fetch error:", error);
      });
  };

  return (
    <Suspense fallback={<div className="loader"></div>} className="main-container">
      <I18nextProvider i18n={i18n}>
        <button
          disabled={availableForSaleError}
          className="btn add-to-cart"
          data-product-id={productId}
          onClick={handleAddToCart}
        >
          <div>
            {isAddingToCart || !hasInitialized ? (
              <span className="loader"></span>
            ) : availableForSaleError ? (
              availableForSaleError
            ) : langCode === LANGUAGE_FR_CODE ? (
              "Ajouter au panier"
            ) : (
              "Add to cart"
            )}
          </div>
        </button>
      </I18nextProvider>
    </Suspense>
  );
};

AddToCart.propTypes = {
  productId: PropTypes.string.isRequired,
};
export default AddToCart;
